import { t } from '@lingui/core/macro'

import { cmmsCurrency } from './cmmsMoney'
import { buildCmmsRecordPdf } from './cmmsRecordPdf'
import type { WorkOrderEntry } from './workOrder'
import type { WorkOrderEvidenceField } from './workOrderEvidence'
import { workOrderFieldReferences } from './workOrderFields'
import type { WorkOrderTextRun } from './workOrderHighlights'
import { workOrderPresentation } from './workOrderPresentation'
import { WorkOrderSheet } from './workOrderSheet'

export interface WorkOrderPdf {
  blob: Blob
  pageCount: number
  runs: WorkOrderTextRun[]
  evidence: WorkOrderEvidenceField[]
}

export async function buildWorkOrderPdf(
  entry: WorkOrderEntry
): Promise<WorkOrderPdf> {
  if (entry.type !== 'cmms-work-order') return buildCmmsRecordPdf(entry)
  const { jsPDF } = await import('jspdf')
  const pdf = new jsPDF({ unit: 'pt', format: 'letter', compress: true })
  const { record: wo, source } = entry.data
  const view = workOrderPresentation(entry)
  const additional = (section: string) =>
    view.additional.filter((field) => field.section === section)
  const sheet = new WorkOrderSheet(pdf, wo.wonum)
  sheet.header(t`Work order`, view.title, source)
  if (view.status) sheet.status(`${t`Status`}: ${view.status}`)
  sheet.y += 14
  sheet.fields(view.context)
  sheet.write(
    t`Description and task text describe work scope; recorded status alone does not verify the work performed.`,
    { size: 9, muted: true }
  )
  sheet.y += 8
  if (view.description) {
    sheet.section(t`Description`)
    sheet.write(view.description, { field: 'description' })
  }
  const justification = additional('priority').find(
    (field) => field.id === 'justifypriority'
  )
  if (justification) {
    sheet.section(t`Priority justification`)
    sheet.write(justification.value, { field: justification.id })
  }
  sheet.section(t`Work logs`, 'worklogsLabel')
  if (!view.logs.length)
    sheet.write(t`No work-log text in this imported record.`, {
      size: 10,
      muted: true,
    })
  for (const log of view.logs)
    sheet.log(log.heading, log.title, log.body, log.id)
  const details = [...view.metadata, ...additional('planning')]
  if (details.length) {
    sheet.section(t`Work details`)
    sheet.fields(details)
  }
  const responsibility = additional('responsibility')
  if (responsibility.length) {
    sheet.section(t`Responsibility`)
    sheet.fields(responsibility)
  }
  const schedule = [...view.schedule, ...additional('schedule')]
  if (schedule.length) {
    sheet.section(t`Schedule`)
    sheet.fields(schedule)
  }
  const totals = additional('totals')
  if (totals.length) {
    sheet.ensure(280)
    sheet.section(t`Labor and cost totals`)
    const currency = cmmsCurrency(wo)
    sheet.write(
      currency
        ? t`Recorded totals in ${currency}. Labor hours measure effort, not elapsed downtime.`
        : t`Recorded totals for this record; currency is not supplied. Labor hours measure effort, not elapsed downtime.`,
      { size: 9, muted: true }
    )
    sheet.y += 10
    const categories = [
      { suffix: 'labhrs', label: t`Labor hours` },
      { suffix: 'labcost', label: t`Labor cost` },
      { suffix: 'matcost', label: t`Material cost` },
      { suffix: 'servcost', label: t`Service cost` },
      { suffix: 'toolcost', label: t`Tool cost` },
    ]
    const rows = categories.flatMap((category) => {
      const values = ['est', 'estatappr', 'act'].map((prefix) => {
        const key = prefix + category.suffix
        return (
          totals.find((field) => field.rawKey === key) ?? {
            id: key,
            label: '',
            value: t`Not supplied`,
          }
        )
      })
      return totals.some((field) => field.rawKey.endsWith(category.suffix))
        ? [{ label: category.label, values }]
        : []
    })
    sheet.table(
      [t`Measure`, t`Current estimate`, t`At approval`, t`Actual`],
      rows
    )
    sheet.write(
      t`Zero totals do not establish that no work or materials were used.`,
      { size: 9, muted: true }
    )
  }
  const materials = additional('materials')
  if (materials.length) {
    sheet.section(t`Material availability`)
    sheet.fields(materials)
    sheet.write(
      t`Availability reflects the recorded calculation, not installed or consumed parts.`,
      { size: 9, muted: true }
    )
  }
  const site = additional('site')
  if (site.length) {
    sheet.section(t`Site-specific fields`)
    sheet.fields(site)
    sheet.write(
      t`Local definitions are unverified. Only source flags set to true are shown; they do not verify permits, compliance or work performed.`,
      { size: 9, muted: true }
    )
  }
  const sourceFields = additional('source')
  sheet.section(t`Source record`)
  sheet.fields([
    ...sourceFields,
    { id: 'sourceVersion', label: t`Source version`, value: source.version },
  ])
  sheet.section(t`Maximo field references`)
  const referenceTitles = [
    t`Work orders`,
    t`Costs on tickets and work orders`,
    t`Material availability status on work orders`,
  ]
  for (const [index, reference] of workOrderFieldReferences.entries()) {
    sheet.reference(referenceTitles[index], reference.url)
  }
  const pageCount = pdf.getNumberOfPages()
  sheet.footer(source)
  return {
    blob: pdf.output('blob'),
    pageCount,
    runs: sheet.runs,
    evidence: view.evidence,
  }
}
